import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Eyebrow } from "../components/Section";
import {
  CAPABILITIES,
  INDUSTRIES,
  ENGAGE_TRIGGERS,
  WHY_BJC,
  BRAND,
} from "../lib/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1633602114554-be8c2c5d6828?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc3NzE1NDc1fDA&ixlib=rb-4.1.0&q=85";

const TEXTURE_IMG =
  "https://images.pexels.com/photos/28608615/pexels-photo-28608615.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--bjc-border)]" data-testid="home-hero">
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--bjc-bg)]/70 via-[var(--bjc-bg)]/85 to-[var(--bjc-bg)]" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-40 pb-24 md:pb-40">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-3 bjc-reveal bjc-reveal-1">
              <Eyebrow num="01">A Specialist Advisory</Eyebrow>
              <p className="mt-6 text-[var(--bjc-text-muted)] text-sm leading-relaxed max-w-xs hidden md:block">
                Independent, regulator-aware, senior-led advisory for institutions facing
                financial crime risk.
              </p>
            </div>
            <div className="md:col-span-9">
              <h1
                data-testid="home-hero-headline"
                className="font-serif-display text-[40px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-[-0.02em] text-[var(--bjc-text)] bjc-reveal bjc-reveal-2"
              >
                Specialist Global Advisory in <span className="italic text-[var(--bjc-gold)]">AML</span>, Financial Crime &amp; Regulatory Defence.
              </h1>
              <p className="mt-10 max-w-2xl text-base md:text-lg text-[var(--bjc-text-muted)] leading-relaxed bjc-reveal bjc-reveal-3">
                We help institutions strengthen, independently assess, and respond to complex
                financial crime and compliance challenges.
              </p>
              <div className="mt-12 flex flex-col sm:flex-row gap-4 bjc-reveal bjc-reveal-4">
                <Link to="/contact" data-testid="hero-speak-cta" className="bjc-btn-primary">
                  Speak With Us <ArrowRight size={14} />
                </Link>
                <Link to="/services" data-testid="hero-services-cta" className="bjc-btn-secondary">
                  Explore Our Services <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-24 md:mt-40 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--bjc-border)] border border-[var(--bjc-border)]">
            {[
              { k: "Focus", v: "AML & Financial Crime" },
              { k: "Approach", v: "Regulator-Aware" },
              { k: "Delivery", v: "Senior-Led" },
              { k: "Lens", v: "India · MEA · APAC" },
            ].map((f) => (
              <div key={f.k} className="bg-[var(--bjc-bg)] p-6 md:p-8">
                <div className="bjc-overline">{f.k}</div>
                <div className="mt-3 font-serif-display text-xl md:text-2xl text-[var(--bjc-text)]">
                  {f.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bjc-section border-b border-[var(--bjc-border)]" data-testid="home-what-we-do">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Eyebrow num="02">What We Do</Eyebrow>
            <h2 className="mt-6 font-serif-display text-3xl md:text-5xl leading-[1.05] text-[var(--bjc-text)]">
              Focused advisory for high-stakes financial crime risk.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-8">
            <p className="text-lg text-[var(--bjc-text)] leading-relaxed">
              {BRAND.shortName} works with financial institutions, fintechs, and cross-border
              businesses to design, review, and strengthen AML and financial crime control
              frameworks.
            </p>
            <p className="text-base text-[var(--bjc-text-muted)] leading-relaxed">
              Our work is centred on one objective — ensuring that compliance is not merely
              documented, but <em className="text-[var(--bjc-gold)] not-italic">effective, defensible,
              and aligned</em> with regulatory expectations.
            </p>
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="bjc-section border-b border-[var(--bjc-border)]" data-testid="home-capabilities">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
            <div className="md:col-span-4">
              <Eyebrow num="03">Core Capabilities</Eyebrow>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] text-[var(--bjc-text)] max-w-[22ch]">
                Four disciplines, one specialist focus.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--bjc-border)] border border-[var(--bjc-border)]">
            {CAPABILITIES.map((c, idx) => (
              <Link
                key={c.id}
                to="/services"
                data-testid={`capability-${c.id}`}
                className="bjc-card p-8 md:p-12 group"
              >
                <div className="flex items-start justify-between gap-6 mb-8">
                  <span className="bjc-numeral">— 0{idx + 1}</span>
                  <ArrowUpRight
                    size={18}
                    className="text-[var(--bjc-text-muted)] group-hover:text-[var(--bjc-gold)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                  />
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--bjc-text)] leading-tight">
                  {c.title}
                </h3>
                <p className="mt-6 text-[var(--bjc-text-muted)] leading-relaxed">
                  {c.summary}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="bjc-section border-b border-[var(--bjc-border)] relative overflow-hidden" data-testid="home-industries">
        <div className="absolute inset-0 -z-10 opacity-15">
          <img src={TEXTURE_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[var(--bjc-bg)]/85" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-4">
              <Eyebrow num="04">Who We Work With</Eyebrow>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] text-[var(--bjc-text)] max-w-[24ch]">
                Serving institutions operating under increasing financial crime risk.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--bjc-border)] border border-[var(--bjc-border)]">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind.title}
                data-testid={`industry-tile-${i}`}
                className="bjc-card p-8 md:p-10"
              >
                <span className="bjc-numeral">— 0{i + 1}</span>
                <h3 className="mt-5 font-serif-display text-xl md:text-2xl text-[var(--bjc-text)]">
                  {ind.title}
                </h3>
                <p className="mt-3 text-sm text-[var(--bjc-text-muted)] leading-relaxed">
                  {ind.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN TO ENGAGE */}
      <section className="bjc-section border-b border-[var(--bjc-border)]" data-testid="home-engage">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Eyebrow num="05">When to Engage Us</Eyebrow>
            <h2 className="mt-6 font-serif-display text-3xl md:text-5xl leading-[1.05] text-[var(--bjc-text)] max-w-[14ch]">
              Engage BJC when it matters most.
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="divide-y divide-[var(--bjc-border)] border-y border-[var(--bjc-border)]">
              {ENGAGE_TRIGGERS.map((t, i) => (
                <li
                  key={t}
                  data-testid={`engage-trigger-${i}`}
                  className="py-5 md:py-6 flex items-start gap-6 group"
                >
                  <span className="bjc-numeral pt-1">0{i + 1}</span>
                  <span className="text-lg md:text-xl text-[var(--bjc-text)] leading-snug group-hover:text-[var(--bjc-gold)] transition-colors">
                    {t}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY BJC */}
      <section className="bjc-section border-b border-[var(--bjc-border)]" data-testid="home-why-bjc">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <div className="md:col-span-4">
              <Eyebrow num="06">Why BJC</Eyebrow>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] text-[var(--bjc-text)] max-w-[20ch]">
                Specialist advisory. Independent judgement. Real-world clarity.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
            {WHY_BJC.map((w, i) => (
              <div key={w.title} data-testid={`why-bjc-${i}`} className="border-t border-[var(--bjc-border)] pt-6">
                <span className="bjc-numeral">— 0{i + 1}</span>
                <h3 className="mt-3 font-serif-display text-xl md:text-2xl text-[var(--bjc-text)]">
                  {w.title}
                </h3>
                <p className="mt-3 text-sm text-[var(--bjc-text-muted)] leading-relaxed">
                  {w.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="bjc-section border-b border-[var(--bjc-border)]" data-testid="home-founder">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <Eyebrow num="07">Founder</Eyebrow>
            <div className="mt-10 flex items-center gap-4">
              <img
                src="/assets/founder.jpg"
                alt="Bharat Jeswani"
                data-testid="founder-photo-small"
                className="w-16 h-16 rounded-full object-cover border border-[var(--bjc-gold)]/40 grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div>
                <div className="font-serif-display text-xl text-[var(--bjc-text)]">Bharat Jeswani</div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--bjc-text-muted)] mt-1">
                  CA · Author of <em className="text-[var(--bjc-gold)] not-italic">The Frauditor</em>
                </div>
              </div>
            </div>
            <div className="bjc-divider mt-10" />
            <p className="mt-6 text-sm text-[var(--bjc-text-faint)] tracking-[0.16em] uppercase">
              A practitioner-led practice
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <h2 className="font-serif-display text-3xl md:text-5xl lg:text-6xl leading-[1.04] text-[var(--bjc-text)]">
              Led by experience and <span className="italic text-[var(--bjc-gold)]">specialisation</span>.
            </h2>
            <p className="mt-10 text-lg text-[var(--bjc-text)] leading-relaxed">
              Bharat Jeswani is a Chartered Accountant and financial crime specialist with extensive
              experience in forensic accounting, AML advisory, and regulatory engagements.
            </p>
            <p className="mt-5 text-base text-[var(--bjc-text-muted)] leading-relaxed">
              He is the author of <em className="not-italic text-[var(--bjc-text)]">The Frauditor</em> and
              a frequent speaker at global forums on financial crime and compliance.
            </p>
            <div className="mt-10">
              <Link to="/about" data-testid="founder-link" className="bjc-link">
                More About the Practice <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bjc-section" data-testid="home-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <Eyebrow num="08" className="justify-center">Start a Conversation</Eyebrow>
          <h2 className="mt-8 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-[var(--bjc-text)] max-w-[20ch] mx-auto">
            If you are evaluating AML effectiveness or responding to regulatory pressure — we can help.
          </h2>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="bottom-speak-cta" className="bjc-btn-primary">
              Speak With Us <ArrowRight size={14} />
            </Link>
            <a href={`mailto:${BRAND.email}`} data-testid="bottom-email-cta" className="bjc-btn-secondary">
              {BRAND.email}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
